/* eslint-disable complexity */
import { LDFlagSet } from 'launchdarkly-react-client-sdk';

import { CurrentUserCheck } from '@futrli/shared-services/authenticator/auth0/types';
import { ForecastListItem } from '@futrli-sdk/predict';
import { WorkspaceMember } from '@futrli-sdk/people';

import { getUserName } from '../../utils/users/getUserName';

export interface ForecastActions {
  delete: boolean;
  duplicate: boolean;
  edit: boolean;
  exportToExcel: boolean;
  exportToGoogleSheets: boolean;
  lock: boolean;
  publish: boolean;
  share: boolean;
  unpublish: boolean;
}

export interface ExtendedForecastListItem extends ForecastListItem {
  actions: ForecastActions;
  isOwner: boolean;
  ownerName?: string;
}

export const processForecast = (
  forecast: ForecastListItem,
  currentUser: CurrentUserCheck,
  members: WorkspaceMember[],
  flags: LDFlagSet = {}
): ExtendedForecastListItem => {
  const hasResourceSharingEnabled = !!flags.resourceSharing;
  const permissionInfo = hasResourceSharingEnabled ? forecast.permission_info : undefined;
  const ownerId = hasResourceSharingEnabled ? forecast.permission_info.owner_id : forecast.owner_id;
  const isOwner = ownerId === currentUser.id;
  const isLiveForecast = forecast.kind === 'live';
  const hasViewPermission =
    !hasResourceSharingEnabled ||
    permissionInfo?.effective_permission === 'view' ||
    permissionInfo?.effective_permission === 'edit';
  const hasEditPermission = !hasResourceSharingEnabled || permissionInfo?.effective_permission === 'edit';
  const isPublished = hasResourceSharingEnabled ? true : forecast.status === 'published';
  const owner = members.find(member => member.id === ownerId);
  const ownerName = owner ? getUserName(owner.name) : undefined;
  const legacyLifecycleActions: Pick<ForecastActions, 'publish' | 'unpublish'> = hasResourceSharingEnabled
    ? { publish: false, unpublish: false } // If resource sharing enabled, publish / unpublish actions are disabled
    : {
        publish: isOwner && !forecast.is_frozen && forecast.status === 'draft',
        unpublish: isOwner && !forecast.is_frozen && forecast.status === 'published'
      };

  return {
    ...forecast,
    isOwner,
    ownerName,
    actions: {
      delete: hasEditPermission && !isLiveForecast && (isOwner || !forecast.is_frozen),
      duplicate: hasViewPermission && !isLiveForecast,
      edit: hasEditPermission && !forecast.is_frozen && !isLiveForecast,
      exportToExcel: hasViewPermission,
      exportToGoogleSheets: hasViewPermission,
      ...legacyLifecycleActions,
      lock: hasEditPermission && forecast.kind === 'budget' && isPublished && !forecast.is_frozen,
      share: hasResourceSharingEnabled && hasEditPermission
    }
  };
};

export const processForecasts = (
  forecasts: ForecastListItem[],
  currentUser: CurrentUserCheck,
  members: WorkspaceMember[],
  flags: LDFlagSet = {}
): ExtendedForecastListItem[] =>
  forecasts
    .filter(forecast => !forecast.is_hidden)
    .map(forecast => processForecast(forecast, currentUser, members, flags));
